import { z } from "zod";
import axiosInstance from "../../axiosInstance";

export const houseAdminDetailsSchema = z.object({
  first_name: z.string().min(1),
  last_name: z.string().min(1),
  phone_number: z.number().min(1).max(15),
});

export type UserProfile = {
  first_name: string;
  last_name: string;
  phone_number: string;
  telegram?: string;
  whatsapp?: string;
  info?: string;
  email?: string;
};

export type UserAddess = { apartment_id: number; address: string };

export type TenantRelationshipUser = {
  relationship_id: number;
  tenant_email: string;
  tenant_personal_code: string;
  role: string;
  is_active: boolean;
  start_date?: string;
  end_date?: string;
}

export type TenantRelationshipApartment = {
  address: string | null;
  apartment_number: number | null;
  apartment_id: number;
  users: TenantRelationshipUser[]
}

export type TenantRelationship = {
  relationships: TenantRelationshipApartment[]
}

export type TenantRelationshipPost = {
  email: string;
  start_date?: string;
  apartment_id: number;
}

export type TenantRelationshipPatch = {
  relationship_id: string;
  end_date: string;
}


export type NotificationType =
  | "enable_sms"
  | "enable_email"
  | "enable_whatsapp"
  | "enable_telegram"
  | "invoice_created"
  | "invoice_to_debt"
  | "house_notice_created";

export type EnableNotification = {
  [key in NotificationType]?: boolean;
};

const userProfileApi = {
  getUserProfile: async (): Promise<UserProfile> => {
    try {
      const response = await axiosInstance.get<UserProfile>(
        "/api/user/profile/"
      );
      return response.data;
    } catch (error) {
      console.error("Error while fetching user profile:", error);
      throw error;
    }
  },

  patchUserProfile: async (data: UserProfile): Promise<UserProfile> => {
    try {
      const response = await axiosInstance.patch<UserProfile>(
        "/api/user/profile/",
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error while updating user profile:", error);
      throw error;
    }
  },

  postLanguage: async (data: {
    language: "en" | "ru" | "lv"
  }): Promise<{ message: string }> => {
    try {
      const response = await axiosInstance.post<{ message: string }>(
        "/api/notifications/preferences/language/",
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error while updating language:", error);
      throw error;
    }
  },

  deleteUser: async (userId: number): Promise<void> => {
    try {
      await axiosInstance.post("/api/user/admin-delete-user/", {
        user_id: userId,
      });
    } catch (error) {
      console.error("Error while deleting user:", error);
      throw error;
    }
  },

  getTenantRelationships: async (): Promise<TenantRelationship> => {
    try {
      const response = await axiosInstance.get<TenantRelationship>(
        "/api/user/tenant-relationships/"
      );
      return response.data;
    } catch (error) {
      console.error("Error while fetching tenant relationships:", error);
      throw error;
    }
  },

  postTenantRelationship: async (data: TenantRelationshipPost): Promise<TenantRelationshipPost> => {
    try {
      const response = await axiosInstance.post<TenantRelationshipPost>(
        "/api/user/tenant-relationships/",
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error while creating tenant relationship:", error);
      throw error;
    }
  },

  patchTenantRelationship: async (data: TenantRelationshipPatch): Promise<TenantRelationshipPatch> => {
    try {
      const response = await axiosInstance.patch<TenantRelationshipPatch>(
        "/api/user/tenant-relationships/",
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error while patch tenant relationship:", error);
      throw error;
    }
  },

  postEnableTelegraNotifications: async (): Promise<{ telegram_link?: string, details?: string }> => {
    try {
      const response = await axiosInstance.post<{ telegram_link?: string, details?: string }>(
        "/api/notifications/telegram-link/"
      );
      return response.data;
    } catch (error) {
      console.error("Error while enable telegram notifications:", error);
      throw error;
    }
  },

  postDisableTelegraNotifications: async ({ channel }: { channel: "sms" | "telegram" | "whatsapp" }): Promise<{ message: string }> => {
    try {
      const response = await axiosInstance.post<{ message: string }>(
        "/api/notifications/unlink-channel/",
        {
          channel
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error while disable telegram notifications:", error);
      throw error;
    }
  },

  postEnableAndDisableEmailNotifications: async (data: {
    enable: boolean
  }): Promise<{ message: string }> => {
    try {
      const response = await axiosInstance.post<{ message: string }>(
        "/api/notifications/email-notifications/toggle/",
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error while enable or disable email notifications:", error);
      throw error;
    }
  },

  getNotificationsPreferences: async (): Promise<EnableNotification> => {
    try {
      const response = await axiosInstance.get<EnableNotification>(
        "/api/notifications/preferences/"
      );
      return response.data;
    } catch (error) {
      console.error("Error while fetching tenant relationships:", error);
      throw error;
    }
  },

  postSwitchingNotificatisPreferencesChannel: async (data: {
    channel: "enable_email" | "enable_sms" | "enable_whatsapp" | "enable_telegram",
    enable: boolean
  }): Promise<{ message: string }> => {
    try {
      const response = await axiosInstance.post<{ message: string }>(
        "/api/notifications/preferences/channel/",
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error while enabling or disabling channel notifications:", error);
      throw error;
    }
  },

  postSwitchingNotificatisPreferencesEvent: async (data: {
    events: Array<"invoice_created" | "invoice_to_debt" | "house_notice_created">,
    enable: boolean
  }): Promise<{ message: string }> => {
    try {
      const response = await axiosInstance.post<{ message: string }>(
        "/api/notifications/preferences/event/",
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error while enable or disable event notifications:", error);
      throw error;
    }
  },

  postVerifyNumberStart: async (data: {
    channel: "sms" | "whatsapp",
    number: string
  }): Promise<{ message: string }> => {
    try {
      const response = await axiosInstance.post<{ message: string }>(
        "/api/notifications/verify/start/",
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error while enable or disable event notifications:", error);
      throw error;
    }
  },

  postVerifyNumberConfirm: async (data: {
    channel: "sms" | "whatsapp",
    number: string,
    code: string
  }): Promise<{ message: string }> => {
    try {
      const response = await axiosInstance.post<{ message: string }>(
        "/api/notifications/verify/confirm/",
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error while enable or disable event notifications:", error);
      throw error;
    }
  },

  postVerifyNumberResend: async (data: {
    channel: "sms" | "whatsapp",
    number: string
  }): Promise<{ message: string }> => {
    try {
      const response = await axiosInstance.post<{ message: string }>(
        "/api/notifications/verify/resend/",
        data
      );
      return response.data;
    } catch (error) {
      const typedError = error as { error: string };
      console.error("Error while resend verify number:", typedError);
      throw typedError;
    }
  },
};

export default userProfileApi;
